import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import SearchField from "../../atoms/inputs/SearchField"
import DatePicker from "../../atoms/inputs/DatePicker"
import Checkbox from "../../atoms/inputs/Checkbox"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import Card from "../../atoms/Card"
import styled from "styled-components"
import Subtitle from "../../atoms/typography/Subtitle"
import dayjs from "dayjs"
import NotificationBar from "../../molecules/NotificationBar"
import { formatApiActivityLog, getActivities } from "../../../utils/formatters"
import { useActivityLog } from "../../../hooks/useActivityLog"
import { NotificationType } from "../../../utils/models"
import FilterDropdown from "../../atoms/inputs/FilterDropdown"
import { activityLogFilterAlternatives, allNotificationTypes, MediaQuery } from "../../../utils/design-helpers"
import Body from "../../atoms/typography/Body"

const ContentContainer = styled.div`
  padding: ${(props) => props.theme.spacings.large};
`

const DatePickerSection = styled.div`
  display: flex;

  ${MediaQuery.XS} {
    justify-content: center;
    width: 100%;
  }
`

const ActivityLog: React.FunctionComponent = () => {
  const { organizationActivities, error, isLoading } = useActivityLog()
  const [allTime, setAllTime] = React.useState(true)
  const [searchValue, setSearchValue] = React.useState<undefined | string>(undefined)
  const [fromDate, setFromDate] = React.useState<undefined | string>(undefined)
  const [toDate, setToDate] = React.useState<undefined | string>(undefined)

  const [selectedFilterIds, setSelectedFilterIds] = React.useState<Array<string | number>>(allNotificationTypes)
  const onClickFilterItem = (id: string | number) => {
    if (selectedFilterIds.includes(id)) {
      const filterd = selectedFilterIds.filter((x) => x !== id)
      setSelectedFilterIds(filterd)
    } else {
      setSelectedFilterIds([...selectedFilterIds, id])
    }
  }
  const onClickSelectAllFilterOptions = () => {
    setSelectedFilterIds(allNotificationTypes)
  }

  const activityNotifications = formatApiActivityLog(organizationActivities?.activityLog ?? [])

  const filteredActivites: Array<NotificationType> = getActivities(
    activityNotifications,
    searchValue,
    allTime,
    fromDate,
    toDate,
    selectedFilterIds,
  )

  const onSearch = (value: any) => {
    setSearchValue(value)
  }
  const onSelectFromDate = (value: string) => {
    setFromDate(value)
    !toDate && setToDate(dayjs().add(1, "day").format("YYYY-MM-DD"))
    setAllTime(false)
  }
  const onSelectToDate = (value: string) => {
    setToDate(value)
    !fromDate && setFromDate(dayjs().format("YYYY-MM-DD"))
    setAllTime(false)
  }
  const onCheckAllTime = () => {
    if (allTime) {
      setAllTime(false)
    } else {
      setAllTime(true)
      setFromDate(undefined)
      setToDate(undefined)
    }
  }

  return (
    <div>
      <Row>
        <Col md={2} xs={6}>
          <SearchField onChange={(e) => onSearch(e)} />
        </Col>
        <Col md={2} xs={6}>
          <FilterDropdown
            items={activityLogFilterAlternatives}
            title="Filter"
            onClickItem={(e) => onClickFilterItem(e)}
            selectedValues={selectedFilterIds}
            selectAllTitle="All types"
            onClickSelectAll={onClickSelectAllFilterOptions}
          />
        </Col>
        <Col md={6}>
          <Visible xs>
            <HorizontalSpacer spacing={2} />
          </Visible>
          <Row align="center" nogutter>
            <DatePicker onSelectDate={onSelectFromDate} value={fromDate} />
            -
            <DatePicker onSelectDate={onSelectToDate} value={toDate} />
            <Hidden xs>
              <Spacer spacing={1} />
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={allTime} onChange={onCheckAllTime} />
                <Spacer spacing={1} /> All time
              </div>
            </Hidden>
          </Row>
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Card>
        <ContentContainer>
          <Subtitle>Activity log</Subtitle>
          <HorizontalSpacer spacing={1} />
          {filteredActivites.map((notification, index) => (
            <NotificationBar
              key={`${notification.id} + ${index}`}
              read={notification.read}
              date={notification.date}
              message={notification.message}
              id={notification.id}
              isUserAction={notification.isUserAction}
            />
          ))}
        </ContentContainer>
      </Card>
      <HorizontalSpacer spacing={3} />
      <Body>
        This page provides an overview of all the activity within your organization, including the addition of new
        members, the formation of groups, and recent purchases.
      </Body>
    </div>
  )
}

export default ActivityLog
